/**
 * The body figure's pure rules — layout, glyph, and the prose a part reads out
 * (M2-06 §3.2), kept apart from any renderer.
 *
 * These began as the private helpers of a hand-rolled imperative `BodyDiagram`
 * that drew the paper-doll in DOM. That class is retired (RC2-03): the library's
 * `@annwn/ui` `BodyDiagram` carries the figure now, and the client feeds it from
 * `condition-view-model.ts`. What survives is only the *decisions* — where a
 * boar's four legs land, which way an arm's slash leans, what a scarred-but-whole
 * part says when you inspect it — because a decision that can only be checked by
 * rendering it is a decision nobody checks. They are pure functions over wire
 * types, tested directly, and shared by the condition overlay and the sheet.
 */

import type { BodyPartSnapshot, WoundSnapshot } from '@annwn/protocol';

import { describeWound, partState, scarLine } from './wound-prose.js';

/**
 * Where each zone sits in the figure, as a CSS grid row.
 *
 * Zones rather than part ids, so a body plan the client has never seen still
 * lands somewhere sensible — and `side` splits left from right within the zone,
 * which is what makes one row of this table serve two arms.
 */
const ZONE_ROW: Readonly<Record<string, number>> = {
  head: 1,
  eye: 1,
  neck: 2,
  chest: 3,
  arm: 3,
  hand: 4,
  abdomen: 4,
  leg: 5,
  foot: 6,
};

/** The glyph each zone is drawn with. Deliberately abstract — a figure, not art. */
const ZONE_GLYPH: Readonly<Record<string, string>> = {
  head: 'O',
  eye: '·',
  neck: '|',
  chest: '█',
  abdomen: '▓',
  arm: '/',
  hand: '•',
  leg: '|',
  foot: '_',
};

/**
 * Where a part sits in the figure, and what it is drawn with.
 *
 * The interesting rules — that a boar's four legs land in the leg row, that an
 * arm leans the way the arm does — are decisions, and are exported so they can be
 * checked without a render.
 */
export function layoutFor(part: BodyPartSnapshot): { row: number; column: number } {
  return { row: ZONE_ROW[part.zone] ?? 3, column: columnFor(part) };
}

/** Left of centre, centre, or right of centre. */
function columnFor(part: BodyPartSnapshot): number {
  if (part.side === 'left') return 1;
  if (part.side === 'right') return 3;
  return 2;
}

export function glyphFor(part: BodyPartSnapshot): string {
  // An arm's slash leans the way the arm does, which is most of what makes the
  // figure read as a figure rather than as a column of boxes.
  if (part.zone === 'arm') return part.side === 'right' ? '\\' : '/';
  return ZONE_GLYPH[part.zone] ?? '+';
}

/**
 * The worst unhealed wound over a part — what colours it, and what the readout
 * talks about. A sum would call a forearm with three scratches ruined.
 */
export function worstWoundOn(
  wounds: readonly WoundSnapshot[],
  partId: string,
): WoundSnapshot | null {
  let worst: WoundSnapshot | null = null;
  for (const wound of wounds) {
    if (wound.part !== partId) continue;
    if (!worst || wound.level > worst.level) worst = wound;
  }
  return worst;
}

/** What a part announces to a screen reader — the same words the colour says. */
export function partLabel(part: BodyPartSnapshot): string {
  const state = partState(part.level);
  const words = state === 'whole' ? 'unhurt' : state;
  return `${part.name}: ${words}${part.bleeding ? ', bleeding' : ''}`;
}

/**
 * The sentence under the figure when a part is inspected.
 *
 * A part with nothing wrong with it still has something to say if something has
 * *been* wrong with it (M4-04): the scars are read out where the wound used to
 * be, which is the only place a player would think to look for them. An open
 * wound still wins the line — what is bleeding now is more urgent than what a
 * wolf did last spring.
 */
export function readoutFor(
  part: BodyPartSnapshot,
  wounds: readonly WoundSnapshot[],
  tick: number,
  scars: readonly WoundSnapshot[] = [],
): string {
  const worst = worstWoundOn(wounds, part.id);
  if (worst) {
    const { title, detail } = describeWound(worst, part.name, tick);
    return `${title} — ${detail}`;
  }

  const onPart = scars.filter((scar) => scar.part === part.id);
  if (onPart.length === 0) return `${capitalize(part.name)}: unhurt.`;

  return onPart.map((scar) => scarLine(scar, part.name, tick)).join(' ');
}

function capitalize(text: string): string {
  return text.slice(0, 1).toUpperCase() + text.slice(1);
}
